import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { PageTabRail } from '../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../../components/router/router-tabbed-navigation';
import { useDocumentPageContext } from '../../../../../../../../../../features/document/document-page-context';
import { DocumentsTable, type DocumentsTableHeaderProps } from '../../../../../../../../../../features/document/table/documents-table';
import { AccountYearSelect } from '../../../../../../../../../../features/registration/account/account-year-select/account-year-select';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';
import type { ViewContext } from '../../../../../../../../../../util/view-context';

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/document/_document/trade-confirmation')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.RegistrationAccountDocuments,
      label: 'Trade Confirmations',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.RegistrationAccountDocuments>,
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `account-${params.accountId}-documents-trade-confirmation`,
        label: 'Documents',
        to: `/registration/account/${params.accountId}/document/trade-confirmation`,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Trade Confirmations'),
  component: AccountTradeConfirmationDocumentsList,
});

function TableHeader(_props: DocumentsTableHeaderProps) {
  const { accountId } = Route.useParams();
  const { year, setYear } = useDocumentPageContext();

  return (
    <PageTabRail
      actions={
        <AccountYearSelect
          accountId={accountId}
          label="Document Year"
          name="trade-confirmation-year-select"
          onChange={(e) => {
            const newYear = parseInt(e.target.value, 10);
            if (!Number.isNaN(newYear)) {
              setYear(newYear);
            }
          }}
          value={year.toString()}
        />
      }
      group={TabbedNavigationGroup.RegistrationAccountDocuments}
    />
  );
}

function AccountTradeConfirmationDocumentsList() {
  const { accountId } = Route.useParams();
  const viewContext: ViewContext = useMemo(
    () => ({
      kind: 'account',
      accountId,
    }),
    [accountId],
  );
  const { year } = useDocumentPageContext();

  return (
    <DocumentsTable
      documentType="tradeConfirmation"
      HeaderActions={TableHeader}
      renderWhenEmpty="No Trade Confirmations"
      view={viewContext}
      year={year}
    />
  );
}
